import { useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertPriority, AlertSeverity, client } from "@/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/custom/button";
import CronInput, { DEFAULT_CRON } from "@/components/custom/cron-input";
import { TzPicker } from "@/routes/dashboard/-tz-picker.tsx";
import { getTimeZone, TimeSettings } from "@/state/time-settings.tsx";
import cronstrue from "cronstrue";
import {
  CheckboxGroupInput,
  CheckboxOption,
} from "@/components/custom/form-inputs/checkbox-group";
import {
  DialogPage,
  MultiStepDialogContent,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content";
import { ThresholdPreviewBar } from "@/components/custom/metric-alerts/threshold-preview-bar";
import {
  ALERT_PRIORITY_VALUES,
  ALERT_SEVERITY_VALUES,
} from "@/lib/alert-settings";
import { ChannelLabel, supportsMetricAlerts } from "@/lib/channel-display";
import {
  resolveQuestion,
  questionsToOptions,
} from "@/routes/dashboard/reports/$reportId/-question-utils";

import { showActionErrorToast } from "@/state/error-toast";

const metricAlertFormSchema = z.object({
  name: z.string().min(1, { message: "Please enter a name" }),
  question: z.string().min(1, { message: "Please select a metric" }),
  operator: z.enum(["BelowThreshold", "AboveThreshold"]),
  thresholdValue: z.coerce.number(),
  priority: z.nativeEnum(AlertPriority),
  severity: z.nativeEnum(AlertSeverity),
  schedule: z.string().min(1, { message: "Please select a frequency" }),
  timezone: z.string().min(1, { message: "Please select a timezone" }),
  channelIds: z
    .array(z.string())
    .min(1, { message: "Select at least one channel" }),
});

type MetricAlertFormValues = z.infer<typeof metricAlertFormSchema>;

interface CreateMetricAlertProps {
  reportId: number;
  filters: string;
  onClose?: () => void;
}

export const CreateMetricAlert = ({
  reportId,
  filters,
  onClose,
}: CreateMetricAlertProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState<MetricAlertFormValues | null>(
    null,
  );

  const form = useForm<MetricAlertFormValues>({
    resolver: zodResolver(metricAlertFormSchema),
    defaultValues: {
      name: "",
      question: "",
      operator: "BelowThreshold",
      thresholdValue: 0,
      priority: AlertPriority.NORMAL,
      severity: AlertSeverity.MEDIUM,
      schedule: DEFAULT_CRON,
      timezone: getTimeZone("local"),
      channelIds: [],
    },
  });

  const question = form.watch("question");
  const operator = form.watch("operator");
  const thresholdValue = form.watch("thresholdValue");

  const { data: metadata } = useQuery(
    client.reporting.getReportMetadata.asQueryOptions({
      id: reportId,
      questionTypes: ["scalar"],
    }),
  );
  const questionOptions = questionsToOptions(metadata?.questions ?? []);
  // Option values carry ids only, so the name and dashboard title come from the metadata.
  const parsedQuestion = resolveQuestion(question, metadata?.questions);

  const { data: channelsData } = useQuery(
    client.alerts.listAlertChannels.asQueryOptions(),
  );
  const configuredChannels = useMemo(
    () =>
      (channelsData?.configuredAlertChannels ?? []).filter(
        supportsMetricAlerts,
      ),
    [channelsData],
  );
  const channelOptions: CheckboxOption[] = configuredChannels.map(
    (channel) => ({
      value: String(channel.id),
      label: <ChannelLabel channel={channel} />,
    }),
  );

  const { data: preview } = useQuery({
    ...client.metricAlerts.preview.asQueryOptions({
      requestBody: {
        reportId,
        // parsedQuestion is only null while `enabled` below is false, so these values are never
        // actually sent - the fallback just satisfies the request body's required `number` fields.
        questionId: parsedQuestion?.id ?? 0,
        dashcardId: parsedQuestion?.dashcardId ?? 0,
        filterScope: filters,
      },
    }),
    enabled: !!parsedQuestion,
  });

  const createMetricAlert = useMutation({
    ...client.metricAlerts.create.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => toast.success(t("successfullyCreatedMetricAlert")),
  });

  const onSubmit = async (values: MetricAlertFormValues) => {
    const parsed = resolveQuestion(values.question, metadata?.questions);
    if (!parsed) {
      toast.error(t("metricNoLongerOnReport"));
      return;
    }
    try {
      await createMetricAlert.mutateAsync({
        requestBody: {
          name: values.name,
          reportId,
          questionId: parsed.id,
          dashcardId: parsed.dashcardId,
          // Store the dashboard-facing title: it is a display label, and it should read the same
          // in the alert list as it does on the report.
          questionName: parsed.title,
          filterScope: filters,
          operator: values.operator,
          thresholdValue: values.thresholdValue,
          priority: values.priority,
          severity: values.severity,
          schedule: values.schedule,
          timezone: values.timezone,
          channelIds: values.channelIds.map((id) => parseInt(id, 10)),
        },
      });
      setSubmitted(values);
    } catch (error) {
      // stay open, error already toasted
    }
  };

  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("enterName")} {...field} />
              </FormControl>
              <FormMessage>{form.formState.errors.name?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("metric")}</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectMetric")} />
                  </SelectTrigger>
                  <SelectContent>
                    {questionOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage>
                {form.formState.errors.question?.message}
              </FormMessage>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, questionOptions],
  );

  const Page2 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="operator"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("condition")}</FormLabel>
              <FormControl>
                <ToggleGroup
                  type="single"
                  value={field.value}
                  onValueChange={(value) => value && field.onChange(value)}
                  className="justify-start"
                >
                  <ToggleGroupItem
                    value="BelowThreshold"
                    className="whitespace-nowrap px-3"
                  >
                    {t("fallsBelow")}
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="AboveThreshold"
                    className="whitespace-nowrap px-3"
                  >
                    {t("risesAbove")}
                  </ToggleGroupItem>
                </ToggleGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="thresholdValue"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("threshold")}</FormLabel>
              <FormControl>
                <Input type="number" {...field} />
              </FormControl>
              <FormMessage>
                {form.formState.errors.thresholdValue?.message}
              </FormMessage>
            </FormItem>
          )}
        />
        <ThresholdPreviewBar
          currentValue={preview?.value ?? null}
          thresholdValue={thresholdValue}
          operator={operator}
        />
        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("priority")}</FormLabel>
              <FormControl>
                <ToggleGroup
                  type="single"
                  value={field.value}
                  onValueChange={(value) => value && field.onChange(value)}
                  className="justify-start"
                >
                  {ALERT_PRIORITY_VALUES.map((priority) => (
                    <ToggleGroupItem
                      key={priority}
                      value={priority}
                      className="whitespace-nowrap px-3"
                    >
                      {priority}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="severity"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("severity")}</FormLabel>
              <FormControl>
                <ToggleGroup
                  type="single"
                  value={field.value}
                  onValueChange={(value) => value && field.onChange(value)}
                  className="justify-start"
                >
                  {ALERT_SEVERITY_VALUES.map((severity) => (
                    <ToggleGroupItem
                      key={severity}
                      value={severity}
                      className="whitespace-nowrap px-3"
                    >
                      {severity}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, preview?.value, thresholdValue, operator],
  );

  const Page3 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="schedule"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>{t("monitoringFrequency")}</FormLabel>
              <FormControl>
                <CronInput value={field.value} onChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex items-center gap-4">
          <label className="text-nowrap">{t("atTimezone")}</label>
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <TzPicker
                className="w-full"
                value={field.value as TimeSettings["timezone"]}
                onChange={(tz) => field.onChange(tz)}
              />
            )}
          />
        </div>
      </div>
    ),
    [form.control, t],
  );

  const Page4 = useMemo(
    () => (
      <FormField
        control={form.control}
        name="channelIds"
        render={({ field }) => (
          <FormItem className="py-2">
            <FormLabel>{t("deliveryChannels")}</FormLabel>
            <FormControl>
              <CheckboxGroupInput options={channelOptions} {...field} />
            </FormControl>
            <FormMessage>
              {form.formState.errors.channelIds?.message}
            </FormMessage>
          </FormItem>
        )}
      />
    ),
    [form.control, t, channelOptions],
  );

  const Page5 = useMemo(() => {
    const values = form.getValues();
    const selectedQuestion = questionOptions.find(
      (option) => option.value === values.question,
    );
    return (
      <div className="space-y-2 text-sm">
        <div>
          <span className="font-medium">{t("name")}:</span> {values.name}
        </div>
        <div>
          <span className="font-medium">{t("metric")}:</span>{" "}
          {selectedQuestion?.label}
        </div>
        <div>
          <span className="font-medium">{t("condition")}:</span>{" "}
          {values.operator === "BelowThreshold"
            ? t("fallsBelow")
            : t("risesAbove")}{" "}
          {values.thresholdValue}
        </div>
        <div>
          <span className="font-medium">{t("priority")}:</span>{" "}
          {values.priority}
        </div>
        <div>
          <span className="font-medium">{t("severity")}:</span>{" "}
          {values.severity}
        </div>
        <div>
          <span className="font-medium">{t("monitoringFrequency")}:</span>{" "}
          {cronstrue.toString(values.schedule, { verbose: true })}
        </div>
        <div>
          <span className="font-medium">{t("atTimezone")}:</span>{" "}
          {values.timezone}
        </div>
        <div>
          <span className="font-medium">{t("deliveryChannels")}:</span>{" "}
          {values.channelIds
            .map(
              (id) =>
                configuredChannels.find((channel) => String(channel.id) === id)
                  ?.channelType,
            )
            .join(", ")}
        </div>
      </div>
    );
  }, [form, questionOptions, configuredChannels, t, currentStep]);

  const pages: DialogPage[] = useMemo(
    () => [
      {
        title: t("nameAndMetric"),
        content: Page1,
        fields: ["name", "question"],
      },
      {
        title: t("condition"),
        content: Page2,
        fields: ["operator", "thresholdValue", "priority", "severity"],
      },
      {
        title: t("frequency"),
        content: Page3,
        fields: ["schedule", "timezone"],
      },
      { title: t("channels"), content: Page4, fields: ["channelIds"] },
      { title: t("summary"), content: Page5, fields: [] },
    ],
    [Page1, Page2, Page3, Page4, Page5, t],
  );

  const handleNext = async () => {
    const fields = pages[currentStep].fields;
    const isStepValid = await form.trigger(
      fields as (keyof MetricAlertFormValues)[],
      { shouldFocus: true },
    );
    if (isStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  if (submitted) {
    return (
      <div className="flex flex-col h-full space-y-4 p-6">
        <div className="text-lg font-medium">{t("alertCreated")}</div>
        <p>
          {t("alertCreatedDescription", {
            name: submitted.name,
            frequency: cronstrue.toString(submitted.schedule, {
              verbose: true,
            }),
            metric:
              resolveQuestion(submitted.question, metadata?.questions)?.title ??
              "",
            operator:
              submitted.operator === "BelowThreshold"
                ? t("fallsBelow")
                : t("risesAbove"),
            threshold: submitted.thresholdValue,
          })}
        </p>
        <div className="flex justify-start mt-auto">
          <Button variant="azure" onClick={() => onClose?.()}>
            {t("done")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form>
        <MultiStepDialogContent
          title={t("setAlertQuery")}
          pages={pages}
          currentStep={currentStep}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={form.handleSubmit(onSubmit)}
          finishButtonText={t("save")}
        />
      </form>
    </Form>
  );
};
